import { useEffect } from "react";

import {
  Popover as NextUIPopover,
  PopoverProps as NextUIPopoverProps,
} from "@nextui-org/react";

import { PopoverContentProps } from "@/ui/SearchPanel/SearchPopover/SearchPopoverContent";
import { cn } from "@/utils/cn";

export type PopoverProps = NextUIPopoverProps &
  PopoverContentProps & {
    placement?: NextUIPopoverProps["placement"] | "fullScreen";
  };

export const Popover = ({
  children,
  motionProps,
  isKeyboardDismissDisabled,
  variant = "search",
  ...props
}: PopoverProps) => {
  const isSearch = variant === "search";

  // detect is open
  const isOpen = props.isOpen ?? props.defaultOpen;

  // lock scroll when open
  useEffect(() => {
    if (isOpen && isSearch) {
      document.documentElement.classList.add("!overflow-hidden");
      document.documentElement.style.setProperty(
        "overflow",
        "hidden",
        "important",
      );

      return () => {
        document.documentElement.classList.remove("!overflow-hidden");
        document.documentElement.style.removeProperty("overflow");
      };
    }
  }, [isOpen, isSearch]);

  return (
    <NextUIPopover
      // disable unwanted focus/keyboard behavior
      shouldCloseOnBlur={false}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled ?? true}
      // disable animation
      disableAnimation={true}
      motionProps={motionProps ?? { variants: {}, transition: { duration: 0 } }}
      isNonModal={false}
      backdrop={"opaque"}
      offset={20}
      shouldBlockScroll={true}
      shouldCloseOnScroll={false}
      updatePositionOnScroll={false}
      triggerScaleOnOpen={false}
      shouldFlip={false}
      shouldUpdatePosition={false}
      color={"default"}
      placement={"bottom-start"}
      classNames={{
        trigger: cn("my-0", {
          "aria-expanded:!z-[999999] aria-expanded:!opacity-100": isSearch,
        }),
        backdrop: "h-dvh w-dvw overflow-hidden",
        base: cn({
          "fixed sm:relative inset-0 mt-32 sm:mt-0 !z-[9999999]": isSearch,
        }),
      }}
      {...props}
    >
      {children}
    </NextUIPopover>
  );
};

Popover.displayName = "Popover";
